import React, { useMemo, useState } from 'react';
import {
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonProgressBar,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import MetricCard from '../components/MetricCard';
import apiClient from '../api/client';
import { formatCurrency, formatDecimal } from '../utils/format';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { useLanguage } from '../context/LanguageContext';
import { RefresherEvent } from '../types/ionic';

interface InventoryItem {
  id: string;
  name: string;
  unit: string;
  currentStock: number;
  minStockLevel: number;
  costPerUnit: number;
  stockValue: number;
  isLowStock: boolean;
  stockPercentage: number;
  warehouseDistribution: Array<{
    warehouseName: string;
    quantity: number;
  }>;
}

interface InventoryReport {
  summary: {
    totalStockValue: number;
    lowStockCount: number;
    totalItems: number;
    averageStockValue: number;
  };
  items: InventoryItem[];
}

const Inventory: React.FC = () => {
  const inventoryQuery = useQuery({
    queryKey: ['inventory', 'report'],
    queryFn: async () => {
      const { data } = await apiClient.get<InventoryReport>(
        '/analytics/inventory/report',
      );
      return data;
    },
  });

  const handleRefresh = async (event: RefresherEvent) => {
    try {
      await inventoryQuery.refetch();
    } finally {
      event.detail.complete();
    }
  };

  const items = inventoryQuery.data?.items ?? [];
  const lowStockItems = items.filter((item) => item.isLowStock);
  const topValued = [...items].sort((a, b) => b.stockValue - a.stockValue).slice(0, 10);
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'low' | 'ok'>('all');

  const filteredItems = useMemo(() => {
    return items
      .filter((item) =>
        item.name.toLowerCase().includes(searchTerm.trim().toLowerCase()),
      )
      .filter((item) => {
        if (statusFilter === 'all') return true;
        const isLow = item.isLowStock;
        return statusFilter === 'low' ? isLow : !isLow;
      });
  }, [items, searchTerm, statusFilter]);

  const stockSummary = inventoryQuery.data?.summary;

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('inventory')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh" />
        </IonRefresher>

        <div style={{ paddingTop: 'var(--ion-safe-area-top)' }}>
          {inventoryQuery.isLoading && !inventoryQuery.data ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <IonSpinner />
            </div>
          ) : (
            <>
              <section>
                <MetricCard
                  title={t('stockValue')}
                  value={formatCurrency(stockSummary?.totalStockValue ?? 0, locale)}
                  context={`${t('averageStockValue') ?? 'Average stock value'}: ${formatCurrency(stockSummary?.averageStockValue ?? 0, locale)}`}
                  accent="primary"
                />
                <MetricCard
                  title={t('lowStockAlerts')}
                  value={`${stockSummary?.lowStockCount ?? 0}`}
                  context={`${stockSummary?.totalItems ?? 0} ${t('materials').toLowerCase()}`}
                  accent="danger"
                />
                <MetricCard
                  title={t('topValuedMaterials')}
                  value={formatCurrency(topValued.reduce((sum, item) => sum + item.stockValue, 0), locale)}
                  context={t('inventoryOverview') ?? 'Inventory overview'}
                  accent="success"
                />
              </section>

              <section style={{ marginTop: 24 }}>
                <IonText color="primary">
                  <h2 style={{ marginBottom: 12 }}>{t('filters') ?? 'Filters'}</h2>
                </IonText>
                <IonSearchbar
                  value={searchTerm}
                  onIonChange={(event) => setSearchTerm(event.detail.value ?? '')}
                  placeholder={t('searchProducts') ?? 'Search materials'}
                />
                <IonSegment
                  style={{ marginTop: 12 }}
                  value={statusFilter}
                  onIonChange={(event) => {
                    const value = (event.detail.value as 'all' | 'low' | 'ok' | undefined) ?? 'all';
                    setStatusFilter(value);
                  }}
                >
                  <IonSegmentButton value="all">
                    <IonLabel>{t('all')}</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="low">
                    <IonLabel>{t('lowStock')}</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="ok">
                    <IonLabel>{t('inStock')}</IonLabel>
                  </IonSegmentButton>
                </IonSegment>
              </section>

              <section style={{ marginTop: 24 }}>
                <IonText color="primary">
                  <h2 style={{ marginBottom: 12 }}>{t('lowStockAlerts')}</h2>
                </IonText>
                {lowStockItems.length === 0 ? (
                  <IonText color="medium">
                    <p>{t('noData')}</p>
                  </IonText>
                ) : (
                  <IonList>
                    {lowStockItems.map((item) => (
                      <IonItem key={item.id}>
                        <IonLabel>
                          <h3 style={{ fontWeight: 600 }}>{item.name}</h3>
                          <p>
                            {item.currentStock} {item.unit} • min {item.minStockLevel}{' '}
                            {item.unit}
                          </p>
                          <IonProgressBar
                            color="danger"
                            value={Math.min(item.stockPercentage / 100, 1)}
                            style={{ marginTop: 8 }}
                          />
                        </IonLabel>
                        <IonText slot="end" color="danger">
                          <strong>{formatCurrency(item.stockValue, locale)}</strong>
                        </IonText>
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </section>

              <section style={{ marginTop: 24, marginBottom: 24 }}>
                <IonText color="primary">
                  <h2 style={{ marginBottom: 12 }}>{t('topValuedMaterials')}</h2>
                </IonText>
                {topValued.length === 0 ? (
                  <IonText color="medium">
                    <p>{t('noData')}</p>
                  </IonText>
                ) : (
                  <IonList>
                    {topValued.map((item) => (
                      <IonItem key={`top-${item.id}`}>
                        <IonLabel>
                          <h3>{item.name}</h3>
                          <p>
                            Stock {item.currentStock} {item.unit} • Cost per unit{' '}
                            {formatCurrency(item.costPerUnit, locale)}
                          </p>
                        </IonLabel>
                        <IonText slot="end" color="primary">
                          <strong>{formatCurrency(item.stockValue, locale)}</strong>
                        </IonText>
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </section>

              <section style={{ marginTop: 24, marginBottom: 24 }}>
                <IonText color="primary">
                  <h2 style={{ marginBottom: 12 }}>{t('inventory')}</h2>
                </IonText>
                {filteredItems.length === 0 ? (
                  <IonText color="medium">
                    <p>{t('noData')}</p>
                  </IonText>
                ) : (
                  <IonList>
                    {filteredItems.map((item) => (
                      <IonItem key={`filtered-${item.id}`}>
                        <IonLabel>
                          <h3 style={{ fontWeight: 600 }}>{item.name}</h3>
                          <p>
                            {t('currentStock')}: {formatDecimal(item.currentStock, { locale, maximumFractionDigits: 2 })}{' '}
                            {item.unit} • {t('minStockLevel')}: {formatDecimal(item.minStockLevel, { locale, maximumFractionDigits: 2 })}{' '}
                            {item.unit}
                          </p>
                        </IonLabel>
                        <IonText slot="end" color={item.isLowStock ? 'danger' : 'success'}>
                          <strong>{formatCurrency(item.stockValue, locale)}</strong>
                        </IonText>
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </section>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Inventory;
